import AlarmCentralContainer        from 'scripts/defa/alarm-centrals/view-alarm-central-container';
import AlarmGroups                  from 'scripts/defa/alarm-groups/collection-alarm-groups';
import ConfigView                   from 'scripts/defa/basic/view-config-view';
import SuccessView                  from 'scripts/defa/basic/view-success-view';
import InsuranceCompanyContainer    from 'scripts/defa/insurance-companies/view-insurance-company-container';
import SubscriptionPackages         from 'scripts/defa/subscription-packages/collection-subscription-packages';
import SubscriptionPackageContainer from 'scripts/defa/subscription-packages/view-subscription-package-container';
import Flow                         from 'scripts/poppet/flow';
import template                     from 'templates/alarmGroupReplaceFlow.jst.html';

/**
 * defa.views.AlarmGroupReplaceFlow
 * A flow to replace subscription package + alarm group.
 * @version 1.2.0
 */

const channel = Bb.Radio.channel( 'objects' );

const selectors = {
	regionContainer: '[data-region=alarm-group-replace-container]',
};

const AlarmGroupReplaceFlow = Flow.extend( {
	
	// ------------------------------------------------
	// Setup
	// ------------------------------------------------
	
	template: template,
	
	regions: {
		container: selectors.regionContainer,
	},
	
	
	// ------------------------------------------------
	// Event Handlers
	// ------------------------------------------------
	
	/**
	 * Triggers when alarmGroups for the current subscription has been fetched.
	 * @param {collection} collection  The alarmGroups collection object.
	 * @param {object}     result      The result from the GET call, object includes arrays for insuranceCompanies,
	 *     alarmCentrals and alarmGroups.
	 * @void
	 */
	onSyncAlarmGroups: function ( collection ) {
		this.alarmGroups = collection;
		this.dispatch();
	},
	
	/**
	 * Triggers when a childView selects an insuranceCompany.
	 * @param {int} id  The id of the selected insuranceCompany.
	 * @void
	 */
	onChildviewInsuranceCompanySelected: function ( id ) {
		this.insuranceCompany = id;
		this.dispatch();
	},
	
	/**
	 * Triggers when a childView selects an alarmCentral.
	 * @param {int} id  The id of the selected alarmCentral
	 * @void
	 */
	onChildviewAlarmCentralSelected: function ( id ) {
		this.alarmCentral = id;
		this.dispatch();
	},
	
	/**
	 * Triggers when we sync a set of available subscriptionPackages.
	 * @param {collection} collection  The collection of subscriptionPackages.
	 * @param {object}     result
	 * @void
	 */
	onSyncSubscriptionPackages: function ( collection ) {
		this.subscriptionPackages = collection;
		this.dispatch();
	},
	
	/**
	 * Triggers when a subscriptionPackage is selected.
	 * @param {int} id  The selected subscriptionPackage id.
	 * @void
	 */
	onChildviewSubscriptionPackageSelected: function ( id ) {
		this.subscriptionPackage = id;
		this.dispatch();
	},
	
	/**
	 * Triggers when a successView asks to close up.
	 * @param {View}  childView  This Flow's SuccessView.
	 * @void
	 */
	onChildviewSuccessClose: function ( childView ) {
		childView.animateOut();
	},
	
	/**
	 * Triggers when a the subscription is saved (patched)
	 * @void
	 */
	onReplacedSubscription: function () {
		this.saved = true;
		this.dispatch();
	},
	
	
	// ------------------------------------------------
	// Other functions
	// ------------------------------------------------
	
	/**
	 * Dispatches gets and views in logical order.
	 * @void
	 */
	dispatch: function () {
		
		let fn = false;
		
		// get alarmgroups
		if ( !this.alarmGroups ) {
			fn = this._fetchAlarmGroups;
		}
		
		// select insuranceCompany
		if ( this.alarmGroups && !this.insuranceCompany ) {
			fn = this._showInsuranceCompanySelection;
		}
		
		// select alarmCentral
		if ( this.alarmGroups && this.insuranceCompany && !this.alarmCentral ) {
			fn = this._showAlarmCentralSelection;
		}
		
		// get subscriptionPackages
		if ( this.alarmGroups && this.insuranceCompany && this.alarmCentral && !this.subscriptionPackages ) {
			fn = this._fetchSubscriptionPackages;
		}
		
		// show the subscriptionPackages
		if ( this.alarmGroups && this.insuranceCompany && this.alarmCentral && this.subscriptionPackages ) {
			fn = this._showSubscriptionPackageSelection;
		}
		
		// confirm change/no-change
		if ( this.alarmGroups && this.insuranceCompany && this.alarmCentral && this.subscriptionPackages && this.subscriptionPackage ) {
			fn = this._saveSubscriptionPackage;
		}
		
		// a new subscription has been saved
		if ( this.saved ) {
			fn = this._showSuccessView;
		}
		
		
		if ( _.isFunction( fn ) ) {
			fn.call( this );
		}
		
	},
	
	
	/**
	 * Fetches a set of AlarmGroups based on subscriptionId.
	 * @void
	 */
	_fetchAlarmGroups: function () {
		
		// don't leave the user hanging
		let configView = new ConfigView( {
			context: 'wait',
		} );
		this.showChildView( 'container', configView );
		
		// create collection, attach listener and fetch data.
		let alarmGroups = new AlarmGroups();
		
		this.listenToOnce( alarmGroups, 'sync', this.onSyncAlarmGroups );
		
		alarmGroups.fetch( {
			data       : {
				subscriptionId: this.options.subscriptionId,
			},
			processData: true,
		} );
		
	},
	
	/**
	 * Shows a selection of insurance companies.
	 * @void
	 */
	_showInsuranceCompanySelection: function () {
		
		let insuranceCompanies = channel.request( 'insuranceCompanies' ).filterByIds( this.alarmGroups.pluck( 'insuranceCompany' ) );
		
		let insuranceCompanyContainer = new InsuranceCompanyContainer( {
			collection   : insuranceCompanies,
			yearlyService: false,
		} );
		
		this.showChildView( 'container', insuranceCompanyContainer );
	},
	
	/**
	 * Shows a selection of alarm centrals.
	 * @void
	 */
	_showAlarmCentralSelection: function () {
		
		let filteredAlarmGroup = new AlarmGroups( this.alarmGroups.where( { insuranceCompany: this.insuranceCompany } ) ),
			alarmCentrals      = channel.request( 'alarmCentrals' ).filterByIds( filteredAlarmGroup.pluck( 'alarmCentral' ) );
		
		let alarmCentralContainer = new AlarmCentralContainer( {
			collection: alarmCentrals,
		} );
		
		this.showChildView( 'container', alarmCentralContainer );
	},
	
	/**
	 * Gets subscriptionPackages to choose from.
	 * @void
	 */
	_fetchSubscriptionPackages: function () {
		
		// don't leave the user hanging
		let configView = new ConfigView( {
			context: 'wait',
		} );
		this.showChildView( 'container', configView );
		
		// create collection, attach listener and fetch data.
		this.alarmGroup = this.alarmGroups.where( {
			insuranceCompany: this.insuranceCompany,
			alarmCentral    : this.alarmCentral,
		} )[ 0 ].get( 'id' );
		
		let subscriptionPackages = new SubscriptionPackages();
		
		this.listenToOnce( subscriptionPackages, 'sync', this.onSyncSubscriptionPackages );
		
		subscriptionPackages.fetch( {
			data       : {
				subscriptionId: this.options.subscriptionId,
				alarmGroupId  : this.alarmGroup,
			},
			processData: true,
		} );
		
	},
	
	/**
	 * Shows a selection of subscriptionPackages.
	 * @void
	 */
	_showSubscriptionPackageSelection: function () {
		
		let subscriptionPackageContainer = new SubscriptionPackageContainer( {
			context   : 'replace',
			collection: this.subscriptionPackages,
		} );
		
		this.showChildView( 'container', subscriptionPackageContainer );
		
	},
	
	/**
	 * Saves the new subscription package with the new alarmgroup.
	 * @void
	 */
	_saveSubscriptionPackage: function () {
		
		this.listenToOnce( this.model, 'sync', this.onReplacedSubscription );
		
		this.model.replacePackage( {
			alarmGroup         : this.alarmGroup,
			subscriptionPackage: this.subscriptionPackage,
			promotionCode      : '',
		} );
		
	},
	
	/**
	 * Shows the success view when all is done.
	 * @void
	 */
	_showSuccessView: function () {
		
		let successView = new SuccessView( {
			context: 'subscription:replaced',
		} );
		
		this.showChildView( 'container', successView );
	},
	
	
} );

export default AlarmGroupReplaceFlow;